// api/Classes.js
import api from "../utils/api";
import { Class } from "../types";

export interface ClassFilterParams {
  search?: string;
  took?: boolean;
  cycleID?: number;
  stateID?: number;
  professionID?: string;
  category?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  userId?: string;
}

export interface ClassFilterResponse {
  classes: Class[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    totalClasses: number;
    completedClasses: number;
    pendingClasses: number;
    totalCredits: number;
  };
}

// Get filtered and paginated classes with stats
export const getFilteredClasses = async (params: ClassFilterParams): Promise<ClassFilterResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.search) queryParams.append('search', params.search);
  if (params.took !== undefined) queryParams.append('took', params.took.toString());
  if (params.cycleID) queryParams.append('cycleID', params.cycleID.toString());
  if (params.stateID) queryParams.append('stateID', params.stateID.toString());
  if (params.professionID) queryParams.append('professionID', params.professionID);
  if (params.category) queryParams.append('category', params.category);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.userId) queryParams.append('userId', params.userId);
  
  const res = await api.get<ClassFilterResponse>(`/api/classes?${queryParams.toString()}`);
  return res.data;
};

// Get classes for the current user (legacy - kept for compatibility)
// If userId is provided, explicitly pass it as a filter
export const getAllClasses = async (filters: Record<string, any> = {}, userId?: string): Promise<Class[]> => {
  const params = { ...filters };
  if (userId) {
    params.userId = userId;
  }
  const res = await api.get(`/api/classes`, { params });
  
  // Handle both old format (array) and new format (object with classes property)
  if (Array.isArray(res.data)) {
    return res.data;
  } else if (res.data.classes) {
    return res.data.classes;
  }
  return [];
};

export const markClassCompleted = async (classId: string, userId: string): Promise<any> => {
  const completedAt = new Date().toISOString();
  // Primary: update the class directly via /api/classes/:id
  const primaryUrl = `/api/classes/${classId}`;
  const primaryBody: any = { took: true, userId, completedAt };
  console.log('markClassCompleted try primary:', { url: primaryUrl, body: primaryBody });
  try {
    const res = await api.put(primaryUrl, primaryBody);
    return res.data;
  } catch (err: any) {
    const status = err?.response?.status;
    const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
    console.warn('Primary completion endpoint failed', { status, serverMsg });
    // Fallback: call a dedicated completion endpoint if present
    const fallbackUrl1 = `/api/classes/complete-class`;
    const fallbackBody: any = {
      classId,
      classid: classId, // compatibility alias some backends expect
      took: true,
      userId,
      completedAt,
    };
    console.log('markClassCompleted try fallback1:', { url: fallbackUrl1, body: fallbackBody });
    try {
      const res2 = await api.put(fallbackUrl1, fallbackBody);
      return res2.data;
    } catch (err2: any) {
      const status2 = err2?.response?.status;
      const serverMsg2 = err2?.response?.data?.message || err2?.response?.data?.error;
      console.warn('Fallback1 endpoint failed', { status: status2, serverMsg: serverMsg2 });
      // Do NOT attempt /api/users/complete-class due to common route conflicts (/api/users/:id)
      // Surface the failure so the UI shows the real server error instead of a misleading cast error
      throw err2;
    }
  }
};

export const getAllCycles = async (): Promise<string[]> => {
  const res = await api.get(`/api/classes/cycles`);
  return res.data;
};

interface NewClassData {
  classname: string;
  classdate: string; // YYYY-MM-DD
  classLU: number;
  isHSW: boolean;
  userId: string;
  // Optional extended fields
  took?: boolean;
  input?: string; // YYYY-MM-DD - date when class was added to system
  cycleid?: number[];
  classlink?: string;
  meetlink?: string;
  memo?: string;
  islecture?: boolean;
  isRegistered?: boolean;
  docpath?: string;
  docOriginalName?: string;
  classwhen?: string;
  classwhere?: string;
  providerno?: string;
  programno?: string;
}

export const addClass = async (classData: NewClassData): Promise<Class> => {
  // Prune empty optional fields to avoid backend validation issues
  const payload: Record<string, any> = {};
  Object.entries(classData).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    payload[k] = v;
  });
  console.log('Class data being sent:', payload);
  try {
    const res = await api.post(`/api/classes`, payload);
    return res.data;
  } catch (error: any) {
    console.log('Full error response:', error.response?.data);
    throw error;
  }
};

export const updateClass = async (classId: string, classData: Partial<NewClassData>): Promise<Class> => {
  const res = await api.put(`/api/classes/${classId}`, classData);
  return res.data;
};

export const getClassById = async (classId: string): Promise<Class> => {
  const res = await api.get(`/api/classes/${classId}`);
  return res.data;
};

export const deleteClass = async (classId: string): Promise<void> => {
  await api.delete(`/api/classes/${classId}`);
};

// Attach a cycle to a class
// Prefers using Mongo _ids: POST /api/classes/:classid/cycles with { cycleId: string }
// Attach a cycle to a class; supports classId as numeric or Mongo _id, and cycle as numeric or Mongo _id
// Backend returns { attached: boolean, already: boolean, message, cycle, class }
// Treat both attached=true and already=true as success
export const attachCycleToClass = async (
  classId: string | number,
  cycleId: string | number
): Promise<any> => {
  const body: any = typeof cycleId === 'number' ? { cycleid: cycleId } : { cycleId };
  const res = await api.post(`/api/classes/${classId}/cycles`, body);
  const data = res.data;
  if (data.attached || data.already) {
    return data;
  }
  throw new Error(data?.message || 'Failed to attach cycle to class');
};

// Remove a cycle from a class (optional helper)
export const removeCycleFromClass = async (classId: string, cycleIdNumeric: number): Promise<any> => {
  const res = await api.delete(`/api/classes/${classId}/cycles/${cycleIdNumeric}`);
  return res.data;
};

  // Upload certificate for a class
  export const uploadCertificate = async (classId: string, file: File): Promise<any> => {
    console.log('[uploadCertificate API] Starting upload:', {
      classId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    const formData = new FormData();
    formData.append('certificate', file);
    formData.append('classId', classId);
  
    console.log('[uploadCertificate API] FormData prepared, sending POST request...');
    
    try {
      // Don't manually set Content-Type - let browser set it with boundary
      // The auth interceptor will add Authorization header automatically
      const res = await api.post('/api/classes/upload-certificate', formData);
      console.log('[uploadCertificate API] Upload successful:', res.data);
      return res.data;
    } catch (error: any) {
      console.error('[uploadCertificate API] Upload failed:', error);
      console.error('[uploadCertificate API] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  };

// Get archived classes
export const getArchivedClasses = async (): Promise<Class[]> => {
  const res = await api.get('/api/classes/archived');
  return res.data;
};

// Toggle archive status
export const toggleArchiveClass = async (classId: string, isArchived: boolean): Promise<Class> => {
  const res = await api.put(`/api/classes/${classId}/archive`, { isArchived });
  return res.data.class;
};

// Get soft-deleted classes (the user's own trash)
export const getDeletedClasses = async (): Promise<Class[]> => {
  const res = await api.get('/api/classes/deleted');
  return res.data;
};

// Restore a soft-deleted class back to the active list
export const restoreClass = async (classId: string): Promise<any> => {
  const res = await api.put(`/api/classes/${classId}/restore`);
  return res.data;
};

// Permanently hard-delete a class that has already been soft-deleted (irreversible)
export const permanentlyDeleteClass = async (classId: string): Promise<any> => {
  const res = await api.delete(`/api/classes/${classId}/permanent`);
  return res.data;
};

// Auto-archive historical classes (admin only)
export const runAutoArchive = async (): Promise<{ message: string; archivedCount: number; criteria: any }> => {
  const res = await api.post('/api/classes/auto-archive');
  return res.data;
};

// Auto-archive current user's historical classes
export const runMyAutoArchive = async (): Promise<{ message: string; archivedCount: number }> => {
  const res = await api.post('/api/classes/auto-archive/me');
  return res.data;
};

